import type {
  PinpointFieldEnum,
  PinpointMappingType,
} from '@/features/comment/models/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import { usePolicies } from '@/features/master-table/policy/_hooks/policy';
import {
  getIndicatorDescriptionLabel,
  getIndicatorTitleLabel,
} from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { ProcessTypeLabelModifier } from '@/features/project/projects/_models/project';
import { useIndicator } from '@/features/project/projects/detail/activities/_hooks/activity';
import { useFundBusinesses } from '@/features/project/projects/detail/fund-bussiness/_hooks/fund-business';
import { FundBusinessFormTitleModifier } from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { useContractorPayments } from '@/features/project/projects/detail/payment/_hooks/payment';
import { usePaymentGroups } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { t } from '@/i18n';
import { useMemo } from 'react';
import type { z } from 'zod';
import { activitiesActivityFlowProjectPathObj } from '../models/pinpoint-path';
import { KsInvestmentPinpointFieldEnum } from '../utils/ks-investment-pinpoint';
import { KsStandardPinpointFieldEnum } from '../utils/ks-standard-pinpoint';
import { pinpointLabelMap } from '../utils/pinpoint-label-map';
import { RsSkippedReviewPinpointFieldEnum } from '../utils/rs-skipped-review-pinpoint';

const useAddDebugPrefix = () => {
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = !!devtoolState?.isDebugMode;

  const addDebugPrefix = ({
    field,
    label,
  }: {
    field: z.infer<typeof PinpointFieldEnum>;
    label: string | undefined;
  }) => {
    if (!isDebugMode) {
      return label;
    }

    const isStartWithRs = field.startsWith('rs.');
    if (isStartWithRs) {
      const isSkipReviewOnly =
        RsSkippedReviewPinpointFieldEnum.options.includes(field);
      if (!isStartWithRs) {
        return isSkipReviewOnly
          ? `【miss:rs対象外】${label}`
          : `【miss:rs】${label}`;
      }
      return isSkipReviewOnly ? `【rs対象外】${label}` : `【rs】${label}`;
    }
    const isStartWithSs = field.startsWith('ss.');
    if (isStartWithSs) {
      return isStartWithSs ? `【ss】${label}` : `【miss:ss】${label}`;
    }

    const isKsInvestment =
      KsInvestmentPinpointFieldEnum.options.includes(field);
    const isKsStandard = KsStandardPinpointFieldEnum.options.includes(field);
    switch (true) {
      case isKsInvestment: {
        const isStartWithKsInvestment = field.startsWith('ks.investment.');
        return isStartWithKsInvestment
          ? `【出資状況表】${label}`
          : `【miss:出資状況表】${label}`;
      }
      case isKsStandard: {
        const isStartWithKsStandard = field.startsWith('ks.standard.');
        return isStartWithKsStandard
          ? `【基金シート】${label}`
          : `【miss:基金シート】${label}`;
      }
      default: {
        const isStartWithKsLocalGovernment = field.startsWith(
          'ks.local-government.',
        );
        return isStartWithKsLocalGovernment
          ? `【地方公共団体等基金】${label}`
          : `【miss:地方公共団体等基金】${label}`;
      }
    }
  };

  return { addDebugPrefix };
};

const formatPinpointLabel = (label: string, name?: string) => {
  return `${label}（${name || t('deleted-item')}）`;
};

type ReturnType = {
  label: string | undefined;
  isLoading: boolean;
} | null;

const useProjectLabel = ({
  projectId,
  expenditureCartId,
  pinpointLabel,
  enabled,
}: {
  projectId: string;
  expenditureCartId: string;
  pinpointLabel: string;
  enabled: boolean;
}): ReturnType => {
  const project = useProject(projectId, enabled);
  const accountingTypeName = useMemo(() => {
    const expenditureCarts = project?.data?.subProjects?.[0]?.expenditureCarts;
    const expenditureCart = expenditureCarts?.find(
      ({ id }) => id === expenditureCartId,
    );
    return expenditureCart?.accountingType.name;
  }, [project?.data, expenditureCartId]);

  if (!enabled) {
    return null;
  }

  return {
    label: formatPinpointLabel(pinpointLabel, accountingTypeName),
    isLoading: project.isPending,
  };
};

const usePolicyLabel = ({
  policyId,
  pinpointLabel,
  enabled,
}: {
  policyId: string;
  pinpointLabel: string;
  enabled: boolean;
}): ReturnType => {
  const policies = usePolicies({ enabled });
  const policyName = policies.data?.find(({ id }) => id === policyId)?.name;
  if (!enabled) {
    return null;
  }
  return {
    label: formatPinpointLabel(pinpointLabel, policyName),
    isLoading: policies.isPending,
  };
};

const paymentGroupLabel = ({
  projectId,
  paymentGroupId,
  pinpointLabel,
  enabled,
  ...paymentAmountBreakdownParams
}: {
  projectId: string;
  paymentGroupId: string;
  pinpointLabel: string;
  enabled: boolean;
} & (
  | { isPaymentAmountBreakdown?: false }
  | {
      isPaymentAmountBreakdown: true;
      paymentId: string;
      amountBreakdownName: string;
    }
)): ReturnType => {
  const paymentGroups = usePaymentGroups({ projectId, enabled });
  const paymentGroup = paymentGroups?.data?.find((item) => {
    return item.id === paymentGroupId;
  });
  if (!enabled) {
    return null;
  }
  if (paymentAmountBreakdownParams.isPaymentAmountBreakdown) {
    const { paymentId, amountBreakdownName } = paymentAmountBreakdownParams;

    const payment = paymentGroup?.payments?.find(({ id }) => id === paymentId);
    const amountBreakdown = payment?.contracts.flatMap(
      (v) => v.amountBreakdown ?? [],
    );
    const hasAmountBreakdown = amountBreakdown?.some(
      ({ name }) => name === amountBreakdownName,
    );
    return {
      label: formatPinpointLabel(
        pinpointLabel,
        hasAmountBreakdown ? amountBreakdownName : undefined,
      ),
      isLoading: paymentGroups.isPending,
    };
  }

  return {
    label: formatPinpointLabel(pinpointLabel, paymentGroup?.name),
    isLoading: paymentGroups.isPending,
  };
};

const useFundBusinessLabel = ({
  projectId,
  fundBusinessId,
  enabled,
}: {
  projectId: string;
  fundBusinessId: string;
  enabled: boolean;
}): ReturnType => {
  const fundBusinesses = useFundBusinesses({ projectId, enabled });

  const label = useMemo(() => {
    const fundBusiness = fundBusinesses?.data?.find(
      (v) => v.id === fundBusinessId,
    );
    const businessForm = fundBusiness?.businessForm;
    const title = fundBusiness?.title;
    return businessForm
      ? `${FundBusinessFormTitleModifier[businessForm]}${title ? `（${title}）` : ''}`
      : undefined;
  }, [fundBusinesses?.data, fundBusinessId]);

  if (!enabled) {
    return null;
  }

  return {
    label,
    isLoading: fundBusinesses.isPending,
  };
};

const usePaymentsLabel = ({
  projectId,
  paymentId,
  pinpointLabel,
}: {
  projectId: string;
  paymentId: string;
  pinpointLabel: string;
}): ReturnType => {
  const payments = useContractorPayments({
    projectId,
  });
  const paymentName = payments?.data?.find((item) => {
    return item.id === paymentId;
  })?.name;
  return {
    label: formatPinpointLabel(pinpointLabel, paymentName),
    isLoading: payments.isPending,
  };
};

export const usePinpointDisplayName = (
  pinpoint: PinpointMappingType | null | undefined,
): { label: string | undefined; isLoading: boolean } => {
  if (!pinpoint) {
    return { label: undefined, isLoading: false };
  }

  const { field, path } = pinpoint;
  const pinpointLabel = pinpointLabelMap(field);
  const { addDebugPrefix } = useAddDebugPrefix();

  const isProjectField =
    field === 'rs.budget-and-execution' ||
    field === 'rs.skippedReview.budget-and-execution';
  const project = useProjectLabel({
    projectId: path.project,
    expenditureCartId: isProjectField ? path.expenditureCart : '',
    pinpointLabel: pinpointLabel,
    enabled: isProjectField,
  });

  const isPolicyField =
    field === 'rs.policy' || field === 'rs.skippedReview.policy';
  const policy = usePolicyLabel({
    policyId: isPolicyField ? path.policy : '',
    pinpointLabel: pinpointLabel,
    enabled: isPolicyField,
  });

  const isPaymentAmountBreakdownField =
    field === 'rs.payment-amount-breakdown-row' ||
    field === 'ss.payment-amount-breakdown-row' ||
    field === 'ks.standard.payment-amount-breakdown-row';

  const isPaymentGroupField =
    field === 'rs.payment-group' ||
    field === 'ss.payment-group-row' ||
    field === 'ks.standard.payment-group-row' ||
    isPaymentAmountBreakdownField;
  const paymentGroup = paymentGroupLabel({
    projectId: path.project,
    paymentGroupId: isPaymentGroupField ? path.paymentGroup : '',
    pinpointLabel: pinpointLabel,
    enabled: isPaymentGroupField,
    ...(isPaymentAmountBreakdownField
      ? {
          isPaymentAmountBreakdown: true,
          paymentId: path.paymentId,
          amountBreakdownName: path.amountBreakdownName,
        }
      : {
          isPaymentAmountBreakdown: false,
        }),
  });

  const isFundBusinessField = field === 'ks.standard.fund-business-table';
  const fundBusiness = useFundBusinessLabel({
    projectId: path.project,
    fundBusinessId: isFundBusinessField ? path.fundBusinessId : '',
    enabled: isFundBusinessField,
  });

  const isPaymentContractorRowField =
    field === 'rs.payment-contractor-row' ||
    field === 'ss.payment-contractor-row';
  const paymentContractorRow = usePaymentsLabel({
    projectId: path.project,
    paymentId: isPaymentContractorRowField ? path.contractorPaymentId : '',
    pinpointLabel: pinpointLabel,
  });

  const isIndicatorField =
    field === 'rs.indicator-title' ||
    field === 'ss.indicator-title' ||
    field === 'ks.standard.indicator-title' ||
    field === 'rs.indicator-description' ||
    field === 'ss.indicator-description' ||
    field === 'ks.standard.indicator-description' ||
    field === 'rs.activity-and-result-target' ||
    field === 'ss.activity-and-result-target' ||
    field === 'ks.standard.activity-and-result-target' ||
    field === 'rs.achievement-for-qualitative-outcome' ||
    field === 'ss.achievement-for-qualitative-outcome' ||
    field === 'ks.standard.achievement-for-qualitative-outcome' ||
    field === 'rs.indicator-setting-reason' ||
    field === 'ss.indicator-setting-reason' ||
    field === 'ks.standard.indicator-setting-reason' ||
    field === 'rs.reason-for-setting-qualitative-outcome' ||
    field === 'ss.reason-for-setting-qualitative-outcome' ||
    field === 'ks.standard.reason-for-setting-qualitative-outcome' ||
    field === 'rs.reason-why-outcomes-cannot-be-multiple' ||
    field === 'ss.reason-why-outcomes-cannot-be-multiple' ||
    field === 'ks.standard.reason-why-outcomes-cannot-be-multiple' ||
    field === 'rs.connection-to-next-outcome' ||
    field === 'ss.connection-to-next-outcome' ||
    field === 'ks.standard.connection-to-next-outcome' ||
    field === 'ks.local-government.connection-to-next-outcome';

  const parsedActivityFlowPath = activitiesActivityFlowProjectPathObj.safeParse(
    pinpoint?.path,
  );
  const indicatorId = parsedActivityFlowPath.success
    ? parsedActivityFlowPath.data.activityIndicatorId
    : '';
  const indicator = useIndicator({
    id: indicatorId,
    enabled: isIndicatorField,
  });

  const formattedLabel = useMemo(() => {
    switch (field) {
      case 'rs.legal-bases':
      case 'ss.legal-bases':
      case 'ks.standard.legal-bases': {
        return formatPinpointLabel(pinpointLabel, path.legalBasis);
      }
      case 'rs.plan':
      case 'ss.plan': {
        return formatPinpointLabel(pinpointLabel, path.plan);
      }
      case 'rs.subsidy-rate-and-so-on': {
        return formatPinpointLabel(pinpointLabel, path.subsidyRate);
      }
      case 'rs.others-indicated-item': {
        return formatPinpointLabel(
          pinpointLabel,
          path.otherIndicatedItemAndResponseReason,
        );
      }
      case 'rs.summary-of-publishing': {
        const name = path.summaryAndResponseProcess
          ? ProcessTypeLabelModifier[path.summaryAndResponseProcess]
          : '';

        return formatPinpointLabel(pinpointLabel, name);
      }

      default: {
        return pinpointLabel;
      }
    }
  }, [pinpointLabel, field, path]);

  if (isProjectField && project) {
    return {
      label: addDebugPrefix({
        field: pinpoint.field,
        label: project.label,
      }),
      isLoading: project.isLoading,
    };
  }

  if (isPolicyField && policy) {
    return {
      label: addDebugPrefix({
        field: pinpoint.field,
        label: policy.label,
      }),
      isLoading: policy.isLoading,
    };
  }

  if (isPaymentGroupField && paymentGroup) {
    return {
      label: addDebugPrefix({
        field: pinpoint.field,
        label: paymentGroup.label,
      }),
      isLoading: paymentGroup.isLoading,
    };
  }

  if (isFundBusinessField && fundBusiness) {
    return {
      label: addDebugPrefix({
        field: pinpoint.field,
        label: fundBusiness.label,
      }),
      isLoading: fundBusiness.isLoading,
    };
  }

  if (isPaymentContractorRowField && paymentContractorRow) {
    return {
      label: addDebugPrefix({
        field: pinpoint.field,
        label: paymentContractorRow.label,
      }),
      isLoading: paymentContractorRow.isLoading,
    };
  }
  if (isIndicatorField) {
    const indicatorTitleField = [
      'rs.indicator-title',
      'ss.indicator-title',
      'ks.standard.indicator-title',
      'ks.local-government.indicator-title',
    ] satisfies z.infer<typeof PinpointFieldEnum>[];

    const indicatorDescriptionField = [
      'rs.indicator-description',
      'ss.indicator-description',
      'ks.standard.indicator-description',
      'ks.local-government.indicator-description',
    ] satisfies z.infer<typeof PinpointFieldEnum>[];

    const isIndicatorTitleField = indicatorTitleField.includes(field);
    const isIndicatorDescriptionField =
      indicatorDescriptionField.includes(field);

    const label = isIndicatorTitleField
      ? getIndicatorTitleLabel(indicator.data?.type)
      : isIndicatorDescriptionField
        ? getIndicatorDescriptionLabel(indicator.data?.type)
        : pinpointLabel;

    return {
      label: addDebugPrefix({
        field: pinpoint.field,
        label,
      }),
      isLoading: indicator.isPending,
    };
  }

  const label = addDebugPrefix({
    field: pinpoint.field,
    label: formattedLabel ?? '',
  });

  return {
    label,
    isLoading: false,
  };
};
